import { useState, useCallback } from 'react';
import api from '../../api';

/**
 * Custom hook for handling suggestion approval/rejection
 * Provides approve and reject functionality with loading states
 */
export const useApproval = (type = 'food') => {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState(null);

  // Get endpoint based on suggestion type
  const getEndpoint = useCallback(() => {
    switch (type) {
      case 'food':
      case 'foodLabel':
        return '/suggested-foods';
      case 'recipe':
        return '/suggested-recipes';
      default:
        return '/suggested-foods';
    }
  }, [type]);

  // Approve a suggestion
  const approve = useCallback(async (suggestionId, updatedData = {}) => {
    setApproving(true);
    setError(null);

    try {
      const endpoint = getEndpoint();
      const response = await api.put(`${endpoint}/approve/${suggestionId}`, updatedData);

      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error(response.data?.error || 'Failed to approve suggestion');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error approving suggestion';
      setError(errorMsg);
      console.error('Error approving suggestion:', err);
      return { success: false, error: errorMsg };
    } finally {
      setApproving(false);
    }
  }, [getEndpoint]);

  // Reject a suggestion
  const reject = useCallback(async (suggestionId, rejectionReason = '') => {
    setRejecting(true);
    setError(null);

    try {
      const endpoint = getEndpoint();
      const response = await api.put(`${endpoint}/reject/${suggestionId}`, {
        rejectionReason
      });

      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error(response.data?.error || 'Failed to reject suggestion');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error rejecting suggestion';
      setError(errorMsg);
      console.error('Error rejecting suggestion:', err);
      return { success: false, error: errorMsg };
    } finally {
      setRejecting(false);
    }
  }, [getEndpoint]);

  return {
    approve,
    reject,
    approving,
    rejecting,
    loading: approving || rejecting,
    error,
    clearError: () => setError(null)
  };
};
